import { useState, useRef, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Avatar } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import { 
  Users, 
  Search, 
  ExternalLink,
  CheckCircle2,
  Circle,
  Loader2
} from 'lucide-react'
import { instagramService } from '@/services'

interface SavedAccountsListProps {
  selectedAccounts: string[]
  onAccountToggle: (accountId: string) => void
}

 

export function SavedAccountsList({ selectedAccounts, onAccountToggle }: SavedAccountsListProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const latestQueryRef = useRef('')

  // Fetch analyzed accounts using the new paged API (same as saved-accounts page)
  const {
    data: paged,
    isLoading: pagedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchPaged,
  } = useInfiniteQuery<{ success: boolean; total_batch: number; profiles: any[]; next_cursor?: string | null }, Error, any, [string, string], string | null>({
    queryKey: ['saved-analytics-paged-watchlist', searchQuery],
    queryFn: async ({ pageParam, signal }) => {
      // Cancel out-of-date requests: only proceed if query matches latest ref
      if (latestQueryRef.current !== searchQuery) {
        throw new Error('stale')
      }
      return instagramService.getSavedAnalyticsPaged({ 
        limit: 20, // Reasonable limit for watchlist creation
        cursor: (pageParam as string | null) ?? null, 
        q: searchQuery || null 
      }, { signal })
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage?.next_cursor ?? undefined),
    enabled: isAuthenticated && !authLoading,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const pagedProfiles = useMemo(() => 
    (paged?.pages || []).flatMap((p: any) => p.profiles || [])
  , [paged])

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search analyzed accounts..."
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value
              latestQueryRef.current = value
              setSearchQuery(value)
              refetchPaged()
            }}
            className="pl-9"
          />
        </div>
        {pagedProfiles.length > 0 && (
          <Badge variant="outline" className="text-xs">
            {pagedProfiles.length}
          </Badge>
        )}
      </div>

      {/* Content */}
      <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
        {/* Initial Loading State */}
        {pagedLoading && pagedProfiles.length === 0 && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 animate-pulse">
                <div className="w-5 h-5 rounded-full bg-muted" />
                <div className="w-10 h-10 rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-3 bg-muted rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!pagedLoading && pagedProfiles.length === 0 && (
          <div className="text-center py-8">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full bg-muted/50">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {searchQuery ? 'No matches found' : 'No analyzed accounts'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchQuery 
                    ? `No accounts match "${searchQuery}"`
                    : 'Analyze accounts from search results to add them to watchlists'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Account Cards */}
        {pagedProfiles.map((profile: any) => {
          const isSelected = selectedAccounts.includes(profile.id)
          const username = profile.target_username || profile.profile_overview?.username
          const fullName = profile.profile_overview?.full_name || username
          const followers = profile.profile_overview?.followers || 0
          const initials = (fullName || username || '').slice(0, 2).toUpperCase()
          
          return (
            <Card 
              key={profile.id} 
              className={`group cursor-pointer transition-all duration-200 ${
                isSelected 
                  ? 'bg-primary/5 border-primary/20' 
                  : 'hover:shadow-md'
              }`}
              onClick={() => onAccountToggle(profile.id)}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    {isSelected ? (
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    )}
                  </div>
                  
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <div className="w-full h-full bg-gradient-to-br from-green-400 to-blue-400 flex items-center justify-center text-white text-sm font-semibold">
                      {initials}
                    </div>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
                        {fullName}
                      </h4>
                      <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 shrink-0">
                        Analyzed
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span className="font-medium">@{username}</span>
                      <span>• {followers >= 1000000 
                        ? `${(followers / 1000000).toFixed(1)}M`
                        : followers >= 1000 
                        ? `${(followers / 1000).toFixed(1)}K`
                        : followers.toString()
                      } followers</span>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(`https://instagram.com/${username}`, '_blank')
                    }}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}

        {/* Load More Button */}
        {hasNextPage && (
          <div className="pt-2">
            <Button 
              onClick={() => fetchNextPage()} 
              disabled={isFetchingNextPage} 
              variant="outline" 
              className="w-full"
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
} 